import { COLORS } from "../../../constants/constant";
import { useCustomDropdown } from "./usecustomdropdown";
import { getTagStyle } from "../../../utils/utils";
import "./donor-tags.scss";

export default function CustomDropdown({
  placeholder = "Select Tags...",
  className = "",
  entityId,
  value = [],
  onChange,
  setError,
}) {
  const {
    // Refs
    dropdownRef,
    triggerRef,
    // State
    selectedTags,
    isOpen,
    search,
    setSearch,
    openActionMenu,
    setOpenActionMenu,
    colorMenuPosition,
    dropdownPosition,
    draggedTag,
    setDraggedTag,
    // Derived
    filtered,
    showCreate,
    selectTitle,
    // Handlers
    handleSelect,
    handleCreate,
    handleColorChange,
    handleRemoveTag,
    handleTagDrop,
    handleDelete,
    handleToggleOpen,
    updateColorMenuPosition,
  } = useCustomDropdown({ value, onChange, setError });

  return (
    <div className={`position-relative w-100 ${className}`} ref={dropdownRef}>
      {/* Trigger input */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggleOpen}
        className="form-control form-control-alternative d-flex align-items-center justify-content-between gap-2 input-container"
        style={{    
          height:
            selectedTags.length > 0 ? "auto" : "calc(1.5em + 1.25rem + 2px)",
        }}
      >
        <span className="d-flex align-items-center flex-wrap gap-1">
          {selectedTags.length > 0 ? (
            <>
            {[...selectedTags]
              .sort((a, b) => a.order - b.order)
              .map((tag, index) => (
                <span
                  key={`tag-${index}-${tag.title}`}
                  draggable
                  className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded"
                  onDragStart={(e) => {
                    setDraggedTag(tag.title);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleTagDrop(tag);
                  }}
                  
                  style={{
                    background: tag.bg,
                    color: getTagStyle(tag.bg).color,
                    fontWeight: 500,
                    cursor: "grab",
                    opacity: draggedTag === tag.title ? 0.6 : 1,
                  }}
                >
                  {tag.title}
                  <i
                    className="fa-solid fa-x"
                    onClick={(e) => {
                      handleRemoveTag(tag);
                    }}
                    style={{ cursor: "pointer", fontSize: 10 }}
                  />
                </span>
              ))}
            <span className="text-muted ms-1" style={{ fontSize: 13, fontWeight: 400, pointerEvents: "none" }}>
                Add tag...
            </span> 
            </>
          ) : (
            <span className="px-2 py-1" style={{ fontWeight: 500 }}>
              {placeholder}
            </span>
          )}
        </span>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="position-fixed bg-white rounded-3 shadow-sm dropdown-panel"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
        >
          {/* Search Input */}
          <div className="p-2">
            <input
              autoFocus
              type="text"
              className="form-control form-control-alternative form-control-sm"
              placeholder="Search or create..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (showCreate) handleCreate();
                }
              }}
              style={{ fontSize: 13 }}
            />
          </div>

          <div
            className="px-3 pb-1"
            style={{ fontSize: 11, color: "#888", fontWeight: 500 }}
          >
            Select an option or create one
          </div>

          {/* Options List */}
          <div className="pb-2" style={{ maxHeight: "300px", overflowY: "auto" }}>
            {filtered.map((opt, index) => {
              const isSelected = selectTitle.has(opt.title);

              return (
                <div
                  key={`opt-${index}-${opt.title} `}
                  className="position-relative d-flex align-items-center justify-content-between gap-2 px-3 py-2"
                  onDragOver={(e) => {
                    if (!isSelected) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    if (!isSelected) return;
                    e.preventDefault();
                    e.stopPropagation();
                    handleTagDrop(opt);
                  }}
                  style={{
                    cursor: "pointer",
                    fontSize: 13,
                    opacity: draggedTag === opt.title ? 0.6 : 1,
                  }}
                  onClick={() => handleSelect(opt)}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <div className="d-flex align-items-center gap-2">
                    {/* Drag Handle */}
                    <span
                      draggable={isSelected}
                      onDragStart={(e) => {
                        if (!isSelected) return;
                        e.stopPropagation();
                        setDraggedTag(opt.title);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => setDraggedTag(null)}
                      style={{
                        color: "#bbb",
                        cursor: isSelected ? "grab" : "default",
                        fontSize: 11,
                        letterSpacing: 1,
                      }}
                    >
                      ::
                    </span>

                    {/* Tag Badge */}
                    <span
                      className="px-2 py-1 rounded"
                      style={{
                        background: opt.bg,
                        color: getTagStyle(opt.bg).color,
                        fontWeight: 500,
                      }}
                    >
                      {opt.title}
                    </span>

                    {isSelected && (
                      <span className="text-muted" style={{ fontSize: 11 }}>
                        Selected
                      </span>
                    )}
                  </div>

                  {/* Action Menu Trigger */}
                  <button
                    type="button"
                    className="btn btn-link p-0 text-muted"
                    onClick={(e) => {
                      e.stopPropagation();
                      updateColorMenuPosition(e.currentTarget);
                      setOpenActionMenu(
                        openActionMenu === opt.title ? null : opt.title,
                      );
                    }}
                    style={{ fontSize: 18, lineHeight: 1, textDecoration: "none" }}
                  >
                    ...
                  </button>

                  {/* Color & Delete Menu */}
                  {openActionMenu === opt.title && (
                    <div
                    className="bg-white rounded shadow-sm py-2 color-menu" 
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      top: colorMenuPosition.top,
                      left: colorMenuPosition.left,
                      minWidth: "160px", 
                    }}
                  >  
                    <div className="position-relative d-flex align-items-center" style={{ width: '100%' }}>
                    <input 
                      type="text" 
                      className="form-control color-menu-input pe-5" 
                      readOnly 
                      value={opt.title} 
                    
                    />
                    <span className="position-absolute end-0 me-3" style={{ cursor: 'pointer', pointerEvents: 'none' }}>
                      <i className="fa-solid fa-circle-info text-secondary fs-5"></i> 
                    </span>
                  </div>
                   <button
                      type="button"
                      className="btn btn-link px-2 mt-1 py-1 d-block w-100 text-start"
                      onClick={() => handleDelete(opt)}
                      style={{ fontSize: 10, textDecoration: "none" }}
                    >
                       <i className="fa-solid fa-trash fs-5 ms-2" style={{color: "#888"}}></i>
                        <span className="ms-3" style={{color:"#888" }}>Delete</span> 
                    </button>
                    <div
                      className="mb-1 px-3 " 
                      style={{ fontSize: 11, marginTop: -10, color: "#888", fontWeight: 500 }}
                    >
                      Color
                    </div> 
                    <div className="d-flex flex-column gap-0 mb-1">
                      {COLORS.map((color) =>  {
                        const isColorSelected = color.bg === opt.bg;
                        return (
                        <button
                          key={color.bg}
                          type="button"
                          className="d-flex align-items-center gap-2 border-0 bg-transparent px-3 py-1.5 menu-item-btn"
                          onClick={() => handleColorChange(opt, color)}
                          style={{
                            fontSize: 13,
                            color: "#555",
                            textAlign: "left"
                          }}
                        > 
                          <div className="d-flex align-items-center gap-2">
                             <span
                            className="color-list"
                            style={{
                              background: color.bg,
                              border: `1px solid ${color.border}`,
                              borderRadius: "2px"
                            }}
                          />
                          {color.name}
                          </div> 
                          {
                            isColorSelected && (
                              <i className="fa-solid fa-check ms-auto text-secondary"></i>
                            )
                          }
                         
                        </button>
                      )})}
                    </div>

                   
                  </div>
                  )}
                </div>
              );
            })}

            {/* Create Option */}
            {showCreate && (
              <div
                onClick={handleCreate}
                className="d-flex align-items-center gap-2 px-3 py-2"
                style={{ cursor: "pointer", fontSize: 13 }}
                
              >
                <span style={{ color: "#888" }}>+</span>
                <span style={{ color: "#555" }}>Create</span>
                <span
                  className="px-2 py-1 rounded create-tag-preview"
                >
                  "{search.trim()}"
                </span>
              </div>
            )}

            {/* Empty State */}
            {filtered.length === 0 && !showCreate && (
              <div
                className="px-3 py-2"
                style={{ fontSize: 13, color: "#aaa" }}
              >
                Type to create a new option
              </div>
            )} 
          </div>
        </div>
      )}
    </div>
  );
}